import { Download, Ellipsis, FileDown, FileSpreadsheet } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Table } from '@/components/ui/table';

import {
    exportTableToCsv,
    exportTableToPdf,
    getPrecedingHeadingText
} from './tableExport';

interface Props {
    children: React.ReactNode;
    tableProps: Record<string, unknown>;
}

export function TableWithExport({ children, tableProps }: Props) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(48);

    useEffect(() => {
        const thead = tableRef.current?.querySelector('thead');
        if (!thead) return;

        const observer = new ResizeObserver(([entry]) => {
            setHeaderHeight(entry.contentRect.height);
        });
        observer.observe(thead);
        return () => observer.disconnect();
    }, []);

    const getTableContext = useCallback(() => {
        const tableEl = tableRef.current?.querySelector('table');
        if (!tableEl) return null;
        const title = getPrecedingHeadingText(wrapperRef.current);
        return { tableEl, title };
    }, []);

    const handleExportCsv = useCallback(() => {
        const ctx = getTableContext();
        if (!ctx) return;
        exportTableToCsv(ctx.tableEl, ctx.title);
    }, [getTableContext]);

    const handleExportPdf = useCallback(() => {
        const ctx = getTableContext();
        if (!ctx) return;
        exportTableToPdf(ctx.tableEl, ctx.title);
    }, [getTableContext]);

    return (
        <div
            ref={wrapperRef}
            className="group/table not-first:mt-6 not-last:mb-2"
        >
            <Card className="relative bg-background shadow-none overflow-hidden">
                <div
                    className="top-0 right-0 z-10 absolute flex items-center opacity-0 has-data-[state=open]:opacity-100 group-hover/table:opacity-100 pr-2 pl-6 transition-opacity"
                    style={{
                        height: headerHeight - 2,
                        background:
                            'linear-gradient(to left, hsl(var(--card)) 80%, transparent)'
                    }}
                >
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="rounded-sm"
                            >
                                <Ellipsis
                                   
                                    strokeWidth={1.75}
                                />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportPdf}>
                                <FileDown />
                                <p className="font-medium text-sm">
                                    Export PDF
                                </p>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportCsv}>
                                <Download />
                                <p className="font-medium text-sm">
                                    Export CSV
                                </p>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div ref={tableRef}>
                    <Table {...(tableProps as any)}>{children}</Table>
                </div>
            </Card>
        </div>
    );
}
